import { Field } from 'formik';
import { APITypes } from '../../../../primary-record-types';
import React from 'react';
import { getIntl } from '../../../../utils/intl';
import { required } from '../../../../utils/validators';
import { handleFormikSubmit } from '../../../../utils/handleFormikSubmit';
import Stack, { FormStack } from '../../../v2BaseComponents/Stack';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { Form, SplitFormRow } from '../../../v2BaseComponents/Form';
import { UserRegistrationDataProvider } from './UserRegistrationDataProvider';
import { AnimateLoadedContent } from '../../../v2BaseComponents/Loader';
import Text from '../../../v2BaseComponents/Text';
import { Platform } from 'react-native';

const intl = getIntl('userRegistrationForm', {
    registration: 'Registration',
    firstName: 'First Name',
    lastName: 'Last Name',
    preferredName: 'Preferred Name',
    dob: 'Date of Birth',
    avatarTitle: 'Profile photo',
    saveButtonText: 'Register',
    subHeader: 'This information is for your user account and not related to a Medical Profile.',
    anonymous: 'Anonymous',
    user: 'User',
    skip: 'Skip',
});

export type UserRegistrationFormFormOwnProps = {
    onUpdate: (values: APITypes.userService.CreateUserBody) => void;
    user: APITypes.userService.User;
};

export type UserRegistrationFormFormProps = UserRegistrationFormFormOwnProps;

const UserRegistrationFormForm: React.FC<UserRegistrationFormFormProps> = ({ onUpdate, user }) => {
    const isIOS = Platform.OS === 'ios';
    // The skip action is rendered outside the Formik render tree, so keep the latest values on a
    // ref to submit when it is pressed — this avoids discarding a name the user has already typed.
    const latestValuesRef = React.useRef<APITypes.userService.User | null>(null);

    const submitValues = (values: APITypes.userService.User): APITypes.userService.User => {
        const submissionValues: APITypes.userService.User = {
            ...values,
            firstName: values.firstName || intl('anonymous'),
            lastName: values.lastName || intl('user'),
        };
        onUpdate({ user: submissionValues, avatar: { type: 'not-modified' } });
        return submissionValues;
    };

    return (
        <UserRegistrationDataProvider initialUserValue={user}>
            {(data) => (
                <AnimateLoadedContent isLoading={data === 'loading'}>
                    {() => {
                        if (data === 'loading') return null;

                        // The name fields are always required for the primary "Register" action. The App
                        // Store forbids gating the app behind a required form, so on iOS we still offer a
                        // "Skip" escape — but only when we don't already know the name (from a pending
                        // invitation or Sign in with Apple). Skipping bypasses validation and proceeds with
                        // a placeholder; it is a deliberate, de-emphasized secondary action, not the default.
                        const hasPrefilledName = Boolean(
                            data.defaultValues.firstName?.trim() && data.defaultValues.lastName?.trim(),
                        );
                        const showSkip = isIOS && !hasPrefilledName;

                        return (
                            <Form
                                centerTitle
                                actionContainerSubmit
                                initialValues={data.defaultValues}
                                title={data.title}
                                saveText={intl('saveButtonText')}
                                // Register is always the primary action and requires a name. When a Skip escape
                                // is offered, render it as a secondary button to the left of Register — a
                                // deliberate, de-emphasized way to proceed without one, not the default.
                                cancelText={intl('skip')}
                                cancelIcon="ArrowRightCircle"
                                cancelButtonVariant="secondary"
                                onCancel={
                                    showSkip
                                        ? () => submitValues(latestValuesRef.current ?? data.defaultValues)
                                        : undefined
                                }
                                onSubmit={(values, helpers) => {
                                    const submissionValues = submitValues(values);
                                    handleFormikSubmit(submissionValues, helpers);
                                }}
                            >
                                {(formikProps) => {
                                    latestValuesRef.current = formikProps.values;
                                    return (
                                        <FormStack>
                                            <Stack horizontal fullWidth justify="center">
                                                <Text variant="body-1">{intl('subHeader')}</Text>
                                            </Stack>
                                            <SplitFormRow>
                                                <Field
                                                    component={FormikInputs.Input}
                                                    name="firstName"
                                                    isRequired
                                                    title={intl('firstName')}
                                                    validate={required}
                                                />
                                                <Field
                                                    component={FormikInputs.Input}
                                                    name="lastName"
                                                    isRequired
                                                    title={intl('lastName')}
                                                    validate={required}
                                                />
                                            </SplitFormRow>
                                            <SplitFormRow>
                                                <Field
                                                    component={FormikInputs.Input}
                                                    name="preferredName"
                                                    title={intl('preferredName')}
                                                />
                                            </SplitFormRow>
                                        </FormStack>
                                    );
                                }}
                            </Form>
                        );
                    }}
                </AnimateLoadedContent>
            )}
        </UserRegistrationDataProvider>
    );
};

export default React.memo(UserRegistrationFormForm);
